import axios from "axios";
import { sendGenericErrorMessage } from "../../utilities/send-generic-error-message";
import Vue from "vue";
import { calculateTaxes } from "../../utilities/tax-calculations";
import { $axios } from "./axios-instance";

const state = {
  printModes: {},
  printModeIds: [],
  availablePrintingModeIds: [],
  printQualityInfo: [],
  availableProcessingSteps: {},
  availableProcessingStepIds: [],
  availableFinishes: {},
  availableFinishIds: [],
};

const mutations = {
  SET_PRINT_MODES(state, printModes) {
    state.printModes = printModes;
  },
  SET_PRINT_MODE_BY_ID(state, printMode) {
    Vue.set(state.printModes, printMode.id, printMode);
    if (state.printModeIds.includes(printMode.id)) return;
    state.printModeIds.push(printMode.id);
  },
  SET_PROCESSING_STEP_BY_ID(state, step) {
    Vue.set(state.availableProcessingSteps, step._id, step);
    if (state.availableProcessingStepIds.includes(step._id)) return;
    state.availableProcessingStepIds.push(step._id);
  },
  SET_FINISH_BY_ID(state, finish) {
    Vue.set(state.availableFinishes, finish._id, finish);
    if (state.availableFinishIds.includes(finish._id)) return;
    state.availableFinishIds.push(finish._id);
  },
  UPDATE_AVAILABLE_PRINTING_MODE_ID(state, newPrintingId) {
    state.availablePrintingModeIds = newPrintingId;
  },
  UPDATE_IMG_FOR_PRINTING_MODE_ID(state, data) {
    state.availablePrintingModeIds[data.printingId].img = data.url;
  },
  UPDATE_SELECTED_COLOR_FOR_PRINTING_MODE_ID(state, data) {
    state.availablePrintingModeIds[data.printingId].selectedColorIndex =
      data.selectedColorIndex;
    state.availablePrintingModeIds[data.printingId].spmId = data.spmId;
  },
  UPDATE_PRINT_QUALITY_INFO(state, printQualityInfo) {
    state.printQualityInfo = printQualityInfo;
  },
};

const API_URL = process.env.VUE_APP_FULFILLMENT_API_BASE_URL;

const actions = {
  fetchPrintModes: async ({ commit }) => {
    try {
      const response = await $axios.get(`${API_URL}/print-modes`, {
        headers: { "x-api-key": process.env.VUE_APP_FULLFILLMENT_API_KEY },
      });
      Object.values(response.data.printModes).forEach((printMode) => {
        commit("SET_PRINT_MODE_BY_ID", printMode);
        printMode.surfaceFinishes.forEach((finish) =>
          commit("SET_FINISH_BY_ID", finish)
        );
      });
    } catch (e) {
      console.log(e);
      sendGenericErrorMessage();
    }
  },
  fetchAvailableProcessingSteps: async ({ commit }) => {
    try {
      /**
       const response = await $axios.get(`${API_URL}/print-modes`, {headers: {'x-api-key': process.env.VUE_APP_FULLFILLMENT_API_KEY}});
       Object.values(response.data.printModes).forEach((printMode) => {
        commit('SET_PRINT_MODE_BY_ID', printMode);
      })
       */
      availableProcessingSteps.forEach((step) => {
        commit("SET_PROCESSING_STEP_BY_ID", step);
      });
    } catch (e) {
      console.log(e);
      sendGenericErrorMessage();
    }
  },
  fetchAvailableFinishes: async ({ commit }) => {
    try {
      const response = await $axios.get(
        `${API_URL}/print-modes/surface-finishes`,
        { headers: { "x-api-key": process.env.VUE_APP_FULLFILLMENT_API_KEY } }
      );
      response.data.surfaceFinishes.forEach((surfaceFinish) => {
        commit("SET_FINISH_BY_ID", surfaceFinish);
      });
    } catch (e) {
      console.log(e);
      sendGenericErrorMessage();
    }
  },
  calculatePricing: async ({ state, dispatch }) => {
    let printModes = [...Object.values(state.printModes)];

    await Promise.all(
      printModes.map(async (printMode) => {
        await dispatch("fetchPrintModePrices", printMode._id);
      })
    );
  },
  fetchPrintModePrices: async ({ commit, state, rootGetters }, printModeId) => {
    let printMode = state.printModes[printModeId];
    if (!printMode) {
      throw new Error(`Printmode with id ${printModeId} does not exist`);
    }
    let current3DFile = rootGetters.current3DFile;
    let currentCartItem = rootGetters.currentCartItem;

    console.log("FETCHING PRICES FOR", printMode.name);

    const updatedPrintMode = { ...printMode };

    const printModeParams = [];

    printMode.surfaceFinishes.forEach((surfaceFinish) => {
      surfaceFinish.colors.forEach((color) => {
        printModeParams.push({
          printModeId: printMode.id,
          surfaceFinishId: surfaceFinish.id,
          printModeColorId: color._id,
          printModeColor: color.name,
        });
      });
    });

    const pricingRequest = {
      objectId: current3DFile.id,
      objectScale: currentCartItem.scale,
      objectUnitLength: current3DFile.givenUnitOfLength,
      objectQuantity: currentCartItem.quantity || 1,
      params: printModeParams,
    };

    const response = await axios.post(
      `${API_URL}/pricing/batch-calculations`,
      pricingRequest,
      {
        headers: {
          "x-api-key": process.env.VUE_APP_FULLFILLMENT_API_KEY,
        },
      }
    );

    const pricingResponses = response.data;

    let counter = 0;
    printMode.surfaceFinishes.forEach((surfaceFinish, sIndex) => {
      surfaceFinish.colors.forEach((color, cIndex) => {
        updatedPrintMode.surfaceFinishes[sIndex].colors[cIndex] = {
          ...color,
          pricing: pricingResponses.find(
            (price) =>
              price.printModeId === printMode.id &&
              price.surfaceFinishId === surfaceFinish.id &&
              price.colorId === color._id
          ),
        };
      });
    });

    commit("SET_PRINT_MODE_BY_ID", updatedPrintMode);
  },
  updateImgUrlForPrintingModeId({ commit, state }, payload) {
    commit("UPDATE_IMG_FOR_PRINTING_MODE_ID", payload);
  },
  updateSelectedColorForPrintingId({ commit, state }, payload) {
    commit("UPDATE_SELECTED_COLOR_FOR_PRINTING_MODE_ID", payload);
  },
  updateMaterialForPrintingModeId({ commit, state }, payload) {
    let availablePrintingModeIds = [...state.availablePrintingModeIds];
    availablePrintingModeIds[payload.printingId] = payload.material;
    commit("UPDATE_AVAILABLE_PRINTING_MODE_ID", availablePrintingModeIds);
  },
};

const getters = {
  printModeById: (state, rootGetters) => (id) => {
    let printMode = state.printModes[id];
    if (printMode) {
      printMode = {
        ...printMode,
        surfaceFinishes: printMode.surfaceFinishes.map((surfaceFinish) => {
          return {
            ...surfaceFinish,
            colors: surfaceFinish.colors.map((color) => {
              let tmpColor = { ...color };
              if (color.pricing && color.pricing.price) {
                tmpColor.pricing = {
                  ...color.pricing,
                  price: calculateTaxes(
                    color.pricing.price,
                    rootGetters.taxRate
                  ),
                };
              }
              return tmpColor;
            }),
          };
        }),
      };
      return printMode;
    }
  },
  // Return a list of printmodes in the order of `allIds`.
  printModes: (state, rootGetters) => {
    return state.printModeIds.map((id) => {
      return rootGetters.printModeById(id);
    });
  },
  availableProcessingSteps(state, rootGetters) {
    return state.availableProcessingStepIds.map((id) => {
      return rootGetters.availableProcessingStepById(id);
    });
  },
  availableProcessingStepById: (state) => (id) => {
    if (state.availableProcessingSteps[id]) {
      return state.availableProcessingSteps[id];
    }
  },
  availableSurfaceFinishes(state, rootGetters) {
    return state.availableFinishIds.map((id) => {
      return rootGetters.availableSurfaceFinishById(id);
    });
  },
  availableSurfaceFinishById: (state) => (id) => {
    if (state.availableFinishes[id]) {
      return state.availableFinishes[id];
    }
  },
  availablePrintingModeIds: (state) => {
    return state.availablePrintingModeIds;
  },
  printQualityInfo: (state, rootGetters) => {
    let printabilityForAll = {
      printable: null,
      tooSmall: true,
      tooLage: true,
    };

    let printabilityForSelected = {
      pricing: null,
      printable: null,
      tooSmall: false,
      tooLage: true,
    };
    Object.values(rootGetters.printModes).forEach((printMode) => {
      printMode.surfaceFinishes.forEach((surfaceFinish) => {
        surfaceFinish.colors.forEach((color) => {
          if (color.pricing) {
            if (color.pricing.printable) {
              printabilityForAll.printable = true;
            }
            if (!color.pricing.tooLarge) {
              printabilityForAll.tooLarge = false;
            }
            if (!color.pricing.tooSmall) {
              printabilityForAll.tooSmall = false;
            }

            if (
              printMode.id === rootGetters.currentCartItem.printModeId &&
              color.id === rootGetters.currentCartItem.color.id &&
              surfaceFinish.id === rootGetters.currentCartItem.surfaceFinishId
            ) {
              printabilityForSelected = color.pricing;
            }
          }
        });
      });
    });

    return { printabilityForAll, printabilityForSelected };
  },
};

export default { state, mutations, actions, getters };
